import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Music2, Plus, UserRound, Users } from 'lucide-react';
import Navbar from '@/components/feature/Navbar';
import SongEditor from './components/SongEditor';
import SongList from './components/SongList';
import SetlistList from './components/SetlistList';
import SetlistDetailView from './components/SetlistDetail';
import AddToSetlistModal from './components/AddToSetlistModal';
import { songsApi, teamsApi, type TeamContext } from '@/api/songs';
import { listMinistries, createMinistry, type Ministry } from '@/api/ministries';
import { useAuthGuard } from '@/hooks/useAuthGuard';
import { useAuth } from '@/hooks/useAuth';
import { apiErrorMessage } from '@/utils/apiError';
import { useMinistryStore } from '@/stores/ministryStore';
import type { PublicSongListItem, Song, SongListItem, Team } from '@/types/song';
import type { Setlist } from '@/types/setlist';

const LibraryPage = () => {
  useAuthGuard();  // Protect this route
  const { user } = useAuth();
  const navigate = useNavigate();
  const setActiveMinistry = useMinistryStore((state) => state.setActiveMinistry);
  const initializingRef = useRef(false);
  const [teams, setTeams] = useState<Team[]>([]);
  const [ministries, setMinistries] = useState<Ministry[]>([]);
  const [teamContexts, setTeamContexts] = useState<TeamContext[]>([]);
  const [team, setTeam] = useState<Team | null>(null);
  const [songs, setSongs] = useState<SongListItem[]>([]);
  const [publicSongs, setPublicSongs] = useState<PublicSongListItem[]>([]);
  const [showPublicSongs, setShowPublicSongs] = useState(false);
  const [publicLoading, setPublicLoading] = useState(false);
  const [selected, setSelected] = useState<Song | null>(null);
  const [search, setSearch] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [editingSong, setEditingSong] = useState<Song | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'songs' | 'setlists'>('songs');
  const [selectedSetlist, setSelectedSetlist] = useState<Setlist | null>(null);
  const [addToSetlistSong, setAddToSetlistSong] = useState<SongListItem | null>(null);
  const canWrite = teamContexts.some(
    (context) => context.team_id === team?.id && ['ADMIN', 'LEADER', 'EDITOR'].includes(context.user_role),
  );
  const ministriesWithTeams = ministries.filter((ministry) =>
    teams.some((availableTeam) => availableTeam.ministry_id === ministry.id),
  );

  // Bootstrap ministry and team selectors, creating defaults only for new users.
  useEffect(() => {
    if (initializingRef.current) return;
    initializingRef.current = true;

    teamsApi.list().then(async (res) => {
      let availableTeams = res.data;
      let availableMinistries = await listMinistries();
      if (availableTeams.length === 0) {
        if (availableMinistries.length === 0) {
          const ministry = await createMinistry('Meu Ministério');
          availableMinistries = [ministry];
        }
        const createdTeam = await teamsApi.create('Minha Banda', availableMinistries[0].id);
        availableTeams = [createdTeam.data];
      }

      setTeams(availableTeams);
      setMinistries(availableMinistries);
      const storedMinistryId = useMinistryStore.getState().activeMinistryId;
      const initialTeam =
        availableTeams.find((availableTeam) => availableTeam.ministry_id === storedMinistryId) ?? availableTeams[0];
      setTeam(initialTeam);
      setActiveMinistry(initialTeam.ministry_id);

      try {
        const ctx = await teamsApi.getContexts();
        setTeamContexts(ctx.data);
      } catch { /* ignore, default false is safe */ }
    }).catch(() => setError('Erro ao carregar times.'));
  }, [setActiveMinistry]);

  const handleSelectMinistry = (ministryId: string) => {
    const nextTeam = teams.find((availableTeam) => availableTeam.ministry_id === ministryId);
    if (!nextTeam) return;
    setActiveMinistry(ministryId);
    setTeam(nextTeam);
    setSelected(null);
    setSelectedSetlist(null);
    setShowEditor(false);
    setEditingSong(null);
    setAddToSetlistSong(null);
    setError('');
  };

  const handleSelectTeam = (teamId: string) => {
    const nextTeam = teams.find((availableTeam) => availableTeam.id === teamId);
    if (!nextTeam) return;
    setTeam(nextTeam);
    setSelectedSetlist(null);
    setAddToSetlistSong(null);
  };

  const fetchSongs = useCallback(() => {
    if (!team) return;
    setLoading(true);
    songsApi.list(team.ministry_id, { search }).then((res) => {
      setSongs(res.data.results);
    }).catch(() => setError('Erro ao carregar músicas.')).finally(() => setLoading(false));
  }, [team, search]);

  useEffect(() => { fetchSongs(); }, [fetchSongs]);

  const fetchPublicSongs = useCallback(() => {
    if (!team) return;
    setPublicLoading(true);
    songsApi.listPublic(team.ministry_id, { search }).then((res) => {
      setPublicSongs(res.data.results);
    }).catch((err) => setError(apiErrorMessage(err, 'Erro ao carregar músicas públicas.')))
      .finally(() => setPublicLoading(false));
  }, [team, search]);

  useEffect(() => {
    if (showPublicSongs) fetchPublicSongs();
  }, [fetchPublicSongs, showPublicSongs]);

  const handleSelect = async (item: SongListItem) => {
    if (!team) return;
    try {
      const res = await songsApi.get(team.ministry_id, item.id);
      setSelected(res.data);
      setShowEditor(false);
      setEditingSong(null);
    } catch {
      setError('Erro ao carregar música.');
    }
  };

  const handleSave = async (payload: { title: string; artist: string; key: string; mode: 'MAJOR' | 'MINOR'; bpm: number | null; content: string; is_public: boolean }) => {
    if (!team) return;
    const sections = [{ type: 'VERSE' as const, title: '', content: payload.content }];
    try {
      if (editingSong) {
        const res = await songsApi.update(team.ministry_id, editingSong.id, { ...payload, sections });
        setSelected(res.data);
      } else {
        const res = await songsApi.create(team.ministry_id, { ...payload, sections });
        setSelected(res.data);
      }
      setShowEditor(false);
      setEditingSong(null);
      fetchSongs();
    } catch (err) {
      setError(apiErrorMessage(err, 'Erro ao salvar música.'));
    }
  };

  const handleDelete = async (id: string) => {
    if (!team) return;
    try {
      await songsApi.delete(team.ministry_id, id);
      if (selected?.id === id) setSelected(null);
      fetchSongs();
    } catch (err) {
      setError(apiErrorMessage(err, 'Erro ao remover música.'));
    }
  };

  const handleDeletePermanently = async (id: string) => {
    if (!team || !window.confirm('Excluir esta música permanentemente de todos os ministérios e setlists?')) return;
    try {
      await songsApi.deletePermanently(team.ministry_id, id);
      if (selected?.id === id) setSelected(null);
      fetchSongs();
    } catch (err) {
      setError(apiErrorMessage(err, 'Erro ao excluir música permanentemente.'));
    }
  };

  const handleToggleShare = async (targetMinistryId: string) => {
    if (!team || !selected) return;
    const isShared = selected.shared_ministry_ids.includes(targetMinistryId);
    try {
      const response = isShared
        ? await songsApi.unshare(team.ministry_id, selected.id, targetMinistryId)
        : await songsApi.share(team.ministry_id, selected.id, targetMinistryId);
      setSelected({ ...selected, shared_ministry_ids: response.data.shared_ministry_ids });
      fetchSongs();
    } catch (err) {
      setError(apiErrorMessage(err, 'Erro ao atualizar compartilhamento.'));
    }
  };

  const handleNew = () => { setEditingSong(null); setShowEditor(true); };
  const handleEdit = async (item: SongListItem) => {
    if (!team) return;
    try {
      const res = await songsApi.get(team.ministry_id, item.id);
      setSelected(res.data);
      setEditingSong(res.data);
      setShowEditor(true);
    } catch (err) {
      setError(apiErrorMessage(err, 'Erro ao carregar música.'));
    }
  };

  const handleSavePublic = async (songId: string) => {
    if (!team) return;
    try {
      await songsApi.savePublic(team.ministry_id, songId);
      setShowPublicSongs(false);
      setSelected(null);
      fetchSongs();
    } catch (err) {
      setError(apiErrorMessage(err, 'Erro ao salvar música pública.'));
    }
  };

  const selectedContent = selected?.latest_version?.sections.map((s) => s.content).join('\n\n') ?? '';
  const canChangePublic = !editingSong || (
    editingSong.created_by_id === user?.id && !editingSong.source_song_id
  );

  return (
    <div className="min-h-screen bg-[#0A0A0F]">
      <Navbar />
      <div className="pt-16 flex h-screen">
        {/* Sidebar */}
        <div className={`w-full md:w-80 lg:w-96 border-r border-white/5 flex flex-col bg-[#0D0C09] ${(showEditor || selected || selectedSetlist) ? 'hidden md:flex' : 'flex'}`}>
          <div className="px-5 pt-5 pb-0 border-b border-white/5">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-white font-semibold text-base" style={{ fontFamily: "'Playfair Display', serif" }}>Biblioteca</h1>
              {activeTab === 'songs' && (
                <button onClick={handleNew} className="w-8 h-8 flex items-center justify-center bg-[#C8A96E] rounded-lg cursor-pointer hover:bg-[#D4B87A] transition-colors">
                  <i className="ri-add-line text-[#0A0A0F] text-sm"></i>
                </button>
              )}
            </div>
            <div className="grid gap-2 pb-3">
              <label className="block">
                <span className="block mb-1 text-[10px] uppercase tracking-wider text-white/40">Ministério das músicas</span>
                <select
                  aria-label="Ministério das músicas"
                  value={team?.ministry_id ?? ''}
                  onChange={(event) => handleSelectMinistry(event.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-xs outline-none focus:border-[#C8A96E]/50"
                >
                  {ministriesWithTeams.map((ministry) => (
                    <option key={ministry.id} value={ministry.id} className="bg-[#0D0C09]">
                      {ministry.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="block mb-1 text-[10px] uppercase tracking-wider text-white/40">Equipe dos setlists</span>
                <select
                  aria-label="Equipe dos setlists"
                  value={team?.id ?? ''}
                  onChange={(event) => handleSelectTeam(event.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-xs outline-none focus:border-[#C8A96E]/50"
                >
                  {teams.filter((availableTeam) => availableTeam.ministry_id === team?.ministry_id).map((availableTeam) => (
                    <option key={availableTeam.id} value={availableTeam.id} className="bg-[#0D0C09]">
                      {availableTeam.name}
                    </option>
                  ))}
                </select>
              </label>
              <p className="text-[10px] leading-4 text-white/35">
                Músicas são compartilhadas no ministério; setlists pertencem à equipe selecionada.
              </p>
            </div>
            {/* Tabs */}
            <div className="flex gap-1 mb-3">
              {(['songs', 'setlists'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => { setActiveTab(tab); setSelected(null); setSelectedSetlist(null); setShowEditor(false); }}
                  className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    activeTab === tab ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white/60'
                  }`}
                >
                  {tab === 'songs' ? 'Músicas' : 'Setlists'}
                </button>
              ))}
            </div>
          </div>

          {error && <p className="text-red-400 text-xs px-5 py-2">{error}</p>}

          {activeTab === 'songs' ? (
            <>
              <div className="px-5 py-3 border-b border-white/5">
                <div className="relative">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-white/30 text-sm"></i>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar músicas..."
                    className="w-full bg-white/5 border border-white/5 rounded-lg pl-9 pr-4 py-2.5 text-white text-sm placeholder-white/20 outline-none focus:border-white/20 transition-colors"
                  />
                </div>
                <div className="grid grid-cols-2 gap-1 mt-3">
                  <button
                    type="button"
                    onClick={() => setShowPublicSongs(false)}
                    className={`py-2 rounded-md text-xs font-medium transition-colors ${!showPublicSongs ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white/70'}`}
                  >
                    Minha biblioteca
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowPublicSongs(true)}
                    className={`py-2 rounded-md text-xs font-medium transition-colors ${showPublicSongs ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white/70'}`}
                  >
                    Catálogo público
                  </button>
                </div>
              </div>
                {showPublicSongs ? (
                  <PublicSongList songs={publicSongs} loading={publicLoading} onSave={handleSavePublic} />
                ) : (
                  <SongList
                    songs={songs}
                    selectedId={selected?.id ?? null}
                    loading={loading}
                    currentUserId={user?.id}
                    onSelect={handleSelect}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onDeletePermanently={handleDeletePermanently}
                  />
                )}
              <div className="px-5 py-3 border-t border-white/5">
                  <p className="text-white/20 text-xs">{showPublicSongs ? publicSongs.length : songs.length} músicas · {team?.name ?? '...'}</p>
              </div>
            </>
          ) : (
            team && (
              <SetlistList
                teamId={team.id}
                canWrite={canWrite}
                onSelect={(sl) => setSelectedSetlist(sl)}
              />
            )
          )}
        </div>

        {/* Main Content */}
        <div className={`flex-1 overflow-y-auto ${(showEditor || selected || selectedSetlist) ? 'block' : 'hidden md:block'}`}>
          {showEditor ? (
            <SongEditor
              song={editingSong ? {
                ...editingSong,
                content: editingSong.latest_version?.sections.map((section) => section.content).join('\n\n') ?? '',
              } : null}
              onSave={handleSave}
              onCancel={() => { setShowEditor(false); setEditingSong(null); }}
              onBack={() => { setShowEditor(false); setEditingSong(null); setSelected(null); }}
              readOnly={false}
              canChangePublic={canChangePublic}
            />
          ) : selected ? (
            <SongViewer
              song={selected}
              content={selectedContent}
              onEdit={() => handleEdit({
                id: selected.id,
                title: selected.title,
                artist: selected.artist,
                key: selected.key,
                mode: selected.mode,
                bpm: selected.bpm,
                created_by_id: selected.created_by_id,
                created_by_name: selected.created_by_name,
                is_public: selected.is_public,
                source_song_id: selected.source_song_id,
                shared_ministry_ids: selected.shared_ministry_ids,
                usage_count: selected.usage_count,
                updated_at: selected.updated_at,
              })}
              onBack={() => setSelected(null)}
              shareableMinistries={ministries.filter((ministry) => ministry.owner_id === user?.id)}
              canManageSharing={selected.created_by_id === user?.id || (selected.is_public && !selected.source_song_id)}
              onToggleShare={handleToggleShare}
              onAddToSetlist={canWrite ? () => setAddToSetlistSong({
                id: selected.id,
                title: selected.title,
                artist: selected.artist,
                key: selected.key,
                mode: selected.mode,
                bpm: selected.bpm,
                created_by_id: selected.created_by_id,
                created_by_name: selected.created_by_name,
                is_public: selected.is_public,
                source_song_id: selected.source_song_id,
                shared_ministry_ids: selected.shared_ministry_ids,
                usage_count: selected.usage_count,
                updated_at: selected.updated_at,
              }) : undefined}
              canEdit={selected.created_by_id === user?.id || (selected.is_public && !selected.source_song_id)}
              editLabel={selected.is_public && !selected.source_song_id && selected.created_by_id !== user?.id
                ? 'Criar cópia para editar'
                : 'Editar'}
            />
          ) : selectedSetlist && team ? (
            <SetlistDetailView
              teamId={team.id}
              ministryId={team.ministry_id}
              setlistId={selectedSetlist.id}
              canWrite={canWrite}
              onBack={() => setSelectedSetlist(null)}
              onDeleted={() => setSelectedSetlist(null)}
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-white/5 mx-auto mb-4">
                  <i className="ri-music-2-line text-white/20 text-2xl"></i>
                </div>
                <p className="text-white/30 text-sm">
                  {activeTab === 'songs' ? 'Selecione uma música ou crie uma nova' : 'Selecione um setlist'}
                </p>
              </div>
            </div>
          )}
        </div>

        {addToSetlistSong && team && (
          <AddToSetlistModal
            teamId={team.id}
            songId={addToSetlistSong.id}
            songTitle={addToSetlistSong.title}
            onClose={() => setAddToSetlistSong(null)}
          />
        )}
      </div>
    </div>
  );
};

const PublicSongList = ({
  songs,
  loading,
  onSave,
}: {
  songs: PublicSongListItem[];
  loading: boolean;
  onSave: (songId: string) => void;
}) => {
  if (loading) {
    return <div className="flex-1 flex items-center justify-center text-white/30 text-sm">Carregando catálogo...</div>;
  }

  if (songs.length === 0) {
    return <div className="flex-1 flex items-center justify-center text-white/30 text-sm">Nenhuma música pública encontrada</div>;
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {songs.map((song) => (
        <article key={song.id} className="border-b border-white/5 px-4 py-4 transition-colors hover:bg-white/[0.025]">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-start gap-2.5">
              <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#C8A96E]/10 text-[#C8A96E]">
                <Music2 size={16} aria-hidden="true" />
              </div>
              <h3 className="min-w-0 break-words pt-1 text-sm font-semibold leading-5 text-white">{song.title}</h3>
            </div>
            <button
              type="button"
              onClick={() => onSave(song.id)}
              disabled={song.is_saved}
              className="flex shrink-0 items-center justify-center gap-1.5 rounded-md border border-white/10 px-2.5 py-2 text-xs text-white/70 hover:bg-white/5 disabled:cursor-default disabled:text-white/30"
              title={song.is_saved ? 'Já está na biblioteca' : 'Salvar na biblioteca'}
            >
              {song.is_saved ? <Check size={14} aria-hidden="true" /> : <Plus size={14} aria-hidden="true" />}
              {song.is_saved ? 'Salva' : 'Salvar'}
            </button>
          </div>

          <div className="ml-10 mt-3 grid grid-cols-2 gap-x-3 gap-y-2">
            <div className="flex min-w-0 items-start gap-1.5 text-xs text-white/55">
              <Music2 size={13} className="mt-0.5 shrink-0 text-white/35" aria-hidden="true" />
              <p className="min-w-0 break-words">
                <span className="mb-0.5 block text-[10px] uppercase text-white/30">Artista</span>
                {song.artist || '—'}
              </p>
            </div>
            <div className="flex min-w-0 items-start gap-1.5 text-xs text-white/55">
              <UserRound size={13} className="mt-0.5 shrink-0 text-white/35" aria-hidden="true" />
              <p className="min-w-0 break-words">
                <span className="mb-0.5 block text-[10px] uppercase text-white/30">Cadastrante</span>
                {song.created_by_name || '—'}
              </p>
            </div>
          </div>

          <p className="ml-10 mt-2 flex items-center gap-1.5 text-xs text-white/35">
            <Users size={13} aria-hidden="true" />
            Usada por {song.usage_count} pessoas
          </p>
        </article>
      ))}
    </div>
  );
};

const SongViewer = ({
  song,
  content,
  onEdit,
  onBack,
  onAddToSetlist,
  shareableMinistries,
  canManageSharing,
  onToggleShare,
  canEdit = true,
  editLabel,
}: {
  song: Song;
  content: string;
  onEdit: () => void;
  onBack: () => void;
  onAddToSetlist?: () => void;
  shareableMinistries: Ministry[];
  canManageSharing: boolean;
  onToggleShare: (ministryId: string) => void;
  canEdit?: boolean;
  editLabel: string;
}) => {
  const [showShareTargets, setShowShareTargets] = useState(false);
  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between px-8 py-4 border-b border-white/5 bg-[#0D0C09]">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="md:hidden w-8 h-8 flex items-center justify-center text-white/50 hover:text-white cursor-pointer"
          >
            <i className="ri-arrow-left-line text-lg"></i>
          </button>
          <div>
            <h2 className="text-white font-semibold text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>{song.title}</h2>
            <p className="text-white/40 text-sm">{song.artist} · Usada por {song.usage_count} pessoas</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {song.bpm && (
            <div className="flex items-center gap-2 bg-white/5 rounded-lg px-3 py-2">
              <i className="ri-time-line text-white/30 text-sm"></i>
              <span className="text-white/60 text-sm">{song.bpm} BPM</span>
            </div>
          )}

          {onAddToSetlist && (
            <button
              onClick={onAddToSetlist}
              className="flex items-center gap-2 bg-white/5 border border-white/10 text-white/60 hover:text-white px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap"
              title="Adicionar ao setlist"
            >
              <i className="ri-list-check-2 text-sm"></i>
            </button>
          )}

          {canManageSharing && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowShareTargets((visible) => !visible)}
                className="flex items-center gap-2 bg-white/5 border border-white/10 text-white/60 hover:text-white px-3 py-2 rounded-lg text-sm transition-colors"
                aria-expanded={showShareTargets}
              >
                <i className="ri-share-line text-sm" />
                Compartilhar
              </button>
              {showShareTargets && (
                <div className="absolute right-0 top-full mt-2 z-30 w-64 max-h-72 overflow-y-auto bg-[#1A1915] border border-white/10 rounded-lg shadow-xl">
                  <p className="px-3 pt-3 pb-2 text-[10px] uppercase tracking-wider text-white/40">
                    Ministérios que você possui
                  </p>
                  {shareableMinistries.map((ministry) => {
                    const isShared = song.shared_ministry_ids.includes(ministry.id);
                    return (
                      <label key={ministry.id} className="flex items-center gap-3 px-3 py-2.5 text-sm text-white/75 hover:bg-white/5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isShared}
                          disabled={
                            isShared &&
                            song.shared_ministry_ids.length === 1 &&
                            !(song.is_public && !song.source_song_id)
                          }
                          onChange={() => onToggleShare(ministry.id)}
                          className="accent-[#C8A96E]"
                        />
                        <span className="truncate">{ministry.name}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {canEdit && (
            <button
              onClick={onEdit}
              className="flex items-center gap-2 bg-white/5 border border-white/10 text-white/60 hover:text-white px-4 py-2 rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-edit-line text-sm" />
              {editLabel}
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-8 py-8">
        <div className="max-w-3xl mx-auto">
          <pre className="text-white/80 text-sm leading-loose font-mono whitespace-pre-wrap">{content || 'Sem conteúdo.'}</pre>
        </div>
      </div>
    </div>
  );
};

export default LibraryPage;
